import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors" data-testid="back-home">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="https://static.prod-images.emergentagent.com/jobs/641f6878-5cef-40eb-8d31-2194fc50afac/images/9204c88f36c8aba1418f399ed70957b9e9b435b9671cfdca88f8cc5e5e0a2467.png" alt="The Humble Hires" className="h-20" />
          </div>
        </div>
      </header>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF0033] mb-4">Legal</p>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight" data-testid="terms-title">Terms & Conditions</h1>
            <p className="text-white/50 mt-4 text-sm">Last updated: April 2026</p>
          </div>

          <div className="space-y-10 text-white/70 text-sm leading-relaxed" data-testid="terms-content">
            <section>
              <h2 className="text-lg font-bold text-white mb-3">1. About Us</h2>
              <p>These terms and conditions apply to all equipment hire services provided by <strong className="text-white">DJ Kayz — The Humble Hires</strong>, based in North West London. By placing a booking through our website or contacting us directly, you agree to be bound by these terms.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">2. Booking & Confirmation</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li>All bookings are made on a request-to-book basis. Submitting a booking request does not guarantee availability.</li>
                <li>Bookings are only confirmed once approved by our team. You will receive a confirmation via email and/or WhatsApp.</li>
                <li>A valid <strong className="text-white">photo ID</strong> (passport or driving licence) must be provided at the time of booking. No exceptions.</li>
                <li>Returning customers who have previously provided ID will have their details on file.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">3. Payment</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li>Payment is accepted via <strong className="text-white">bank transfer</strong> only.</li>
                <li>A <strong className="text-white">50% deposit</strong> is required to secure your booking. The remaining balance must be paid no later than <strong className="text-white">48 hours before</strong> the event date.</li>
                <li>Full payment details (bank account) will be provided upon booking confirmation.</li>
                <li>Failure to pay the remaining balance within the required timeframe may result in cancellation of your booking.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">4. Cancellation & Refund Policy</h2>
              <div className="glass-panel p-6 border-[#FF0055]/20">
                <p className="text-white font-bold mb-2">No refunds will be issued under any circumstances.</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Once a booking is confirmed and payment has been made, no refunds will be provided.</li>
                  <li>If you need to change your event date, you may request to <strong className="text-white">reschedule</strong> subject to availability and admin approval.</li>
                  <li>Rescheduling requests must be made as early as possible. We cannot guarantee alternative dates will be available.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">5. Delivery, Setup & Collection</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li>Orders over <strong className="text-white">£100</strong> within a <strong className="text-white">4-mile radius</strong> of our base in North West London qualify for <strong className="text-[#39FF14]">free delivery</strong>.</li>
                <li>For locations outside the 4-mile radius, a delivery fee will be calculated and communicated upon booking confirmation.</li>
                <li>Setup and collection are included with every hire.</li>
                <li>The client must ensure suitable access to the venue for delivery and collection of equipment.</li>
                <li>Equipment must be accessible and ready for collection at the agreed time. Additional charges may apply for late collection due to client delays.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">6. Equipment Care & Damage</h2>
              <div className="glass-panel p-6 border-[#FF6B35]/20">
                <ul className="space-y-2 list-disc list-inside">
                  <li>The client is <strong className="text-white">fully responsible</strong> for all hired equipment from the time of delivery until collection.</li>
                  <li>Equipment must not be tampered with, modified, or moved to a different location without prior approval.</li>
                  <li>If any equipment is <strong className="text-white">damaged, lost, or stolen</strong> during the hire period, the client will be liable for the full cost of repair or replacement.</li>
                  <li>Any damage must be reported immediately. Do not attempt to repair equipment yourself.</li>
                  <li>Equipment must be kept in a dry, safe environment. It must not be exposed to rain, excessive moisture, or extreme conditions.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">7. Use of Equipment</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li>Equipment must only be used for its intended purpose.</li>
                <li>The client is responsible for ensuring that the use of equipment complies with local noise regulations and venue rules.</li>
                <li>DJ Kayz — The Humble Hires accepts no liability for any noise complaints, fines, or legal action arising from the client's use of the equipment.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">8. Liability</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li>DJ Kayz — The Humble Hires will not be held liable for any injury, loss, or damage arising from the use of hired equipment.</li>
                <li>The client assumes all risk associated with the operation and use of the equipment during the hire period.</li>
                <li>We are not responsible for power supply issues, venue restrictions, or any other factors outside our control that may affect the use of the equipment.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">9. Force Majeure</h2>
              <p>DJ Kayz — The Humble Hires shall not be liable for any failure to perform obligations due to circumstances beyond our reasonable control, including but not limited to severe weather, road closures, vehicle breakdown, or government restrictions.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">10. Privacy & Data</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li>Personal information and photo ID provided during booking will be stored securely and used solely for the purpose of managing your booking.</li>
                <li>We will not share your personal data with third parties unless required by law.</li>
                <li>By placing a booking, you consent to the storage and processing of your personal information as described above.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">11. Changes to Terms</h2>
              <p>We reserve the right to update these terms and conditions at any time. Any changes will be reflected on this page with an updated date. Continued use of our services constitutes acceptance of the revised terms.</p>
            </section>

            <section className="border-t border-white/10 pt-8">
              <h2 className="text-lg font-bold text-white mb-3">Contact</h2>
              <p>If you have any questions about these terms, please get in touch:</p>
              <div className="mt-4 space-y-1">
                <p><strong className="text-white">DJ Kayz — The Humble Hires</strong></p>
                <p>North West London</p>
                <p>WhatsApp: <a href="https://wa.me/447543710680" className="text-[#FF0033] hover:underline">07543 710 680</a></p>
                <p>Email: <a href="mailto:thehumblehires@gmail.com" className="text-[#FF0033] hover:underline">thehumblehires@gmail.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
